import { Link, useNavigate } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import {
  ArrowsClockwiseIcon,
  CaretDownIcon,
  CircleNotchIcon,
  GraduationCapIcon,
  GitDiffIcon,
  SealCheckIcon,
  SparkleIcon,
} from '@phosphor-icons/react';
import type { AgentSopVersionEntry, SopVersionOrigin } from '@erp/rpc';
import { useSopVersions } from 'src/api/rpc/hooks/agent/useOperatorSops';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { cn } from 'src/lib/utils';
import paths from 'src/paths';

const interactiveRow =
  'cursor-pointer transition-colors hover:bg-accent hover:text-accent-foreground data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground';

/**
 * Which actor produced a version — the chip that keeps an agentic self-heal visibly distinct
 * from a human edit in the history (AI-461). No chip for a plain human edit: it is the default
 * case, and stamping every row would bury the exceptions this exists to surface.
 */
export function SopVersionOriginChip({ origin }: { origin: SopVersionOrigin }) {
  const gt = useGT();
  switch (origin) {
    case 'self_heal':
      return (
        <Badge
          variant="outline"
          className="h-4 gap-0.5 border-amber-300 px-1 text-[10px] text-amber-700"
          title={gt(
            'The replay engine repaired a broken step and republished.',
            {
              context:
                'tooltip on the self-heal origin chip of a procedure version',
            },
          )}
        >
          <ArrowsClockwiseIcon className="h-2.5 w-2.5" />
          <T context="procedure version origin chip: replay self-heal">
            healed
          </T>
        </Badge>
      );
    case 'ai_edit':
      return (
        <Badge
          variant="outline"
          className="h-4 gap-0.5 border-purple-300 px-1 text-[10px] text-purple-700"
          title={gt('Edited by the assistant at your request.', {
            context:
              'tooltip on the AI-edit origin chip of a procedure version',
          })}
        >
          <SparkleIcon className="h-2.5 w-2.5" weight="fill" />
          <T context="procedure version origin chip: Nami edit">AI</T>
        </Badge>
      );
    case 'interview':
      return (
        <Badge
          variant="outline"
          className="h-4 gap-0.5 px-1 text-[10px] text-muted-foreground"
          title={gt('Taught by demonstration or imported.', {
            context:
              'tooltip on the interview origin chip of a procedure version',
          })}
        >
          <GraduationCapIcon className="h-2.5 w-2.5" />
          <T context="procedure version origin chip: taught by interview">
            taught
          </T>
        </Badge>
      );
    case 'builtin_install':
      return (
        <Badge
          variant="outline"
          className="h-4 px-1 text-[10px] text-muted-foreground"
          title={gt('Installed from the built-in procedure catalog.', {
            context:
              'tooltip on the builtin-install origin chip of a procedure version',
          })}
        >
          <T context="procedure version origin chip: built-in install">
            built-in
          </T>
        </Badge>
      );
    case 'json_import':
      return (
        <Badge
          variant="outline"
          className="h-4 px-1 text-[10px] text-muted-foreground"
          title={gt('Imported from an exported procedure JSON file.', {
            context:
              'tooltip on the JSON-import origin chip of a procedure version',
          })}
        >
          <T context="procedure version origin chip: imported from JSON">
            JSON
          </T>
        </Badge>
      );
    case 'human_edit':
      return null;
  }
}

function StatusBadge({
  version,
  compact,
}: {
  version: Pick<AgentSopVersionEntry, 'status' | 'isPinned'>;
  compact?: boolean;
}) {
  const sizing = compact ? 'h-4 px-1 text-[10px]' : undefined;
  if (version.status === 'draft') {
    return (
      <Badge variant="secondary" className={sizing}>
        <T context="procedure version status">Draft</T>
      </Badge>
    );
  }
  if (version.isPinned) {
    return (
      <Badge className={sizing}>
        <SealCheckIcon
          className={cn('mr-0.5', compact ? 'h-2.5 w-2.5' : 'h-3 w-3')}
          weight="fill"
        />
        <T context="procedure version status">Published</T>
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className={sizing}>
      <T context="procedure version status">Finalized</T>
    </Badge>
  );
}

export { StatusBadge as SopVersionStatusBadge };

/**
 * The "v6 ✓Published ⌄ → Jump to version" control (AI-461), a sibling of
 * `AgentVersionPickerDropdown` with one addition: the origin chip, so a self-heal or Nami edit
 * is visible from the picker without opening the Versions tab.
 */
export function SopVersionPickerDropdown({
  sopId,
  organizationId,
  currentVersionNumber,
  currentStatus,
  currentIsPinned,
  versionSwitchDisabled,
  onShowDiff,
}: {
  sopId: string;
  organizationId: string | undefined;
  currentVersionNumber: number;
  currentStatus: 'draft' | 'finalized';
  currentIsPinned: boolean;
  /** True while editing a draft — rows grey out; the version is fixed until publish/discard. */
  versionSwitchDisabled: boolean;
  onShowDiff?: () => void;
}) {
  const gt = useGT();
  const navigate = useNavigate();
  const { data, isLoading } = useSopVersions(organizationId, sopId);
  const versions = data?.versions ?? [];

  const openVersion = (version: AgentSopVersionEntry) => {
    // The published version is the canonical bare URL; anything else is an explicit /v/N.
    if (version.isPinned) {
      void navigate({
        to: paths.authenticated.backgroundAgents.procedureDetail,
        params: { id: sopId },
      });
    } else {
      void navigate({
        to: paths.authenticated.backgroundAgents.procedureVersionDetail,
        params: { id: sopId, versionNumber: String(version.version) },
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5"
          title={
            versionSwitchDisabled
              ? gt('Publish or discard this draft before switching versions.', {
                  context:
                    'tooltip when procedure version switching is disabled during draft editing',
                })
              : gt('Switch version', {
                  context: 'tooltip for the procedure version picker',
                })
          }
        >
          <span className="font-mono">{`v${currentVersionNumber}`}</span>
          <StatusBadge
            compact
            version={{ status: currentStatus, isPinned: currentIsPinned }}
          />
          <CaretDownIcon className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[380px]">
        <DropdownMenuLabel>
          <T context="procedure version picker menu heading">Jump to version</T>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <CircleNotchIcon className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          </div>
        ) : versions.length === 0 ? (
          <div className="px-2 py-2 text-xs text-muted-foreground">
            <T context="empty procedure version picker">No versions.</T>
          </div>
        ) : (
          versions.map((version) => {
            const isCurrent = version.version === currentVersionNumber;
            const disabled = isCurrent || versionSwitchDisabled;
            return (
              <DropdownMenuItem
                key={version.id}
                disabled={disabled}
                aria-current={isCurrent ? 'true' : undefined}
                onClick={() => {
                  if (!disabled) openVersion(version);
                }}
                className={cn(
                  'flex items-center justify-between gap-2',
                  !disabled && interactiveRow,
                  disabled &&
                    'cursor-default bg-muted/50 text-muted-foreground data-[disabled]:opacity-100',
                )}
              >
                <div className="flex min-w-0 flex-1 items-center gap-1.5">
                  <span className="font-mono text-xs font-medium">
                    {`v${version.version}`}
                  </span>
                  <StatusBadge compact version={version} />
                  <SopVersionOriginChip origin={version.origin} />
                  {version.createdByName && (
                    <span className="ml-auto truncate text-xs text-muted-foreground">
                      {version.createdByName}
                    </span>
                  )}
                </div>
                {isCurrent && (
                  <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
                    <T context="currently viewed procedure version">Viewing</T>
                  </span>
                )}
              </DropdownMenuItem>
            );
          })
        )}
        <DropdownMenuSeparator />
        {onShowDiff && (
          <DropdownMenuItem
            onClick={onShowDiff}
            disabled={versions.length < 2}
            className={interactiveRow}
          >
            <GitDiffIcon className="size-4" />
            <span className="flex flex-col">
              <T context="open SOP version comparison">Show Diff</T>
              {versions.length < 2 && (
                <span className="text-xs text-muted-foreground">
                  <T context="SOP diff unavailable explanation">
                    At least two versions are needed to compare.
                  </T>
                </span>
              )}
            </span>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild className={interactiveRow}>
          <Link
            to={paths.authenticated.backgroundAgents.procedureVersions}
            params={{ id: sopId }}
          >
            <T context="procedure version picker link to full history">
              View all versions →
            </T>
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
